// The page bundles this file too: it imports nothing, so the designer, the Studio and the server clamp alike.

// Where a logo sits within its spot's printable box, from the spot's standard position (centered, at the top), in
// inches. Positive X moves right as the viewer sees the photo; positive Y moves down. Absent means 0.
export interface Offset {
  offsetXIn: number;
  offsetYIn: number;
}

export const OFFSET_STEP = 0.25;

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const toStep = (v: number) => Math.round(v / OFFSET_STEP) * OFFSET_STEP;
// The furthest whole step inside a limit, so a clamped offset stays on the quarter-inch grid.
const stepsWithin = (limit: number) => (limit > 0 ? Math.floor(limit / OFFSET_STEP + 1e-9) * OFFSET_STEP : 0);
// -0 reads as 0 in JSON but not in Object.is: keep it plain.
const plain = (v: number) => (v === 0 ? 0 : v);

// The offset rounded to quarter inches and kept inside the spot: X within half the room left beside the logo either
// way, Y from the top down to the room left under it. A missing or unreadable value is 0. The server always re-clamps.
export function clampOffset(
  spot: { maxWidthIn: number; maxHeightIn: number },
  widthIn: number,
  heightIn: number,
  o: Partial<Offset> | null | undefined,
): Offset {
  const x = stepsWithin((spot.maxWidthIn - widthIn) / 2);
  const y = stepsWithin(spot.maxHeightIn - heightIn);
  return {
    offsetXIn: plain(Math.min(x, Math.max(-x, toStep(num(o?.offsetXIn))))),
    offsetYIn: plain(Math.min(y, Math.max(0, toStep(num(o?.offsetYIn))))),
  };
}

export function isZeroOffset(o: Partial<Offset> | null | undefined): boolean {
  return num(o?.offsetXIn) === 0 && num(o?.offsetYIn) === 0;
}

// The two fields only when the logo moved, so a placement at the standard position stores and reads as it always did.
export function offsetFields(o: Partial<Offset> | null | undefined): Partial<Offset> {
  return isZeroOffset(o) ? {} : { offsetXIn: num(o?.offsetXIn), offsetYIn: num(o?.offsetYIn) };
}

const inches = (n: number) => Number(Math.abs(n).toFixed(2)).toString();

// "Standard position", or "1.5 in lower, 0.5 in left": up or down first, then sideways.
export function offsetText(o: Partial<Offset> | null | undefined): string {
  const x = num(o?.offsetXIn);
  const y = num(o?.offsetYIn);
  const parts = [
    ...(y === 0 ? [] : [`${inches(y)} in ${y > 0 ? "lower" : "higher"}`]),
    ...(x === 0 ? [] : [`${inches(x)} in ${x > 0 ? "right" : "left"}`]),
  ];
  return parts.length === 0 ? "Standard position" : parts.join(", ");
}
